import type { Vec2 } from "./types";

/** Stable old centers and field IDs; authored shoreline and explicit walkable bridges. */
export const REGIONS = [
  {
    id: "garden",
    name: "青屿庭园",
    x: 800,
    y: 485,
    rx: 530,
    ry: 272,
    color: "#759b65",
    note: "八块灵田 · 灵泉与工坊",
  },
  {
    id: "grove",
    name: "丰穰林地",
    x: -100,
    y: 485,
    rx: 530,
    ry: 320,
    color: "#51946d",
    note: "野莓、药草、木材 · 四块试验田",
  },
  {
    id: "quarry",
    name: "琥珀矿原",
    x: 1700,
    y: 485,
    rx: 530,
    ry: 320,
    color: "#9a9260",
    note: "琥珀凝露、石料 · 四块试验田",
  },
] as const;

export const BRIDGES = [
  { x: 350, y: 485, width: 170, height: 66 },
  { x: 1250, y: 485, width: 170, height: 66 },
];
const coastCache = new Map<string, [number, number][]>();
/** Shared, smoothly sampled shoreline. Fine irregularity is deterministic, never random routing. */
export function coast(r: (typeof REGIONS)[number]): [number, number][] {
  const cached = coastCache.get(r.id);
  if (cached) return cached;
  const outline: [number, number][] = [
    [-0.755, 0],
    [-0.72, -0.38],
    [-0.56, -0.7],
    [-0.3, -0.9],
    [0.02, -0.96],
    [0.35, -0.87],
    [0.6, -0.66],
    [0.73, -0.34],
    [0.755, 0],
    [0.72, 0.4],
    [0.54, 0.72],
    [0.28, 0.88],
    [-0.04, 0.92],
    [-0.35, 0.81],
    [-0.58, 0.69],
    [-0.73, 0.39],
  ];
  const anchors: [number, number][] = outline.map(([x, y], i) => [
    r.x + x * r.rx,
    r.y +
      (y +
        (r.id === "grove"
          ? Math.sin(i * 2) * 0.035
          : r.id === "quarry"
            ? Math.cos(i * 1.8) * 0.035
            : 0)) *
        r.ry,
  ]);
  const smooth: [number, number][] = [];
  for (let i = 0; i < anchors.length; i++) {
    const p0 = anchors[(i + 15) % 16],
      p1 = anchors[i],
      p2 = anchors[(i + 1) % 16],
      p3 = anchors[(i + 2) % 16];
    for (let step = 0; step < 8; step++) {
      const t = step / 8,
        t2 = t * t,
        t3 = t2 * t;
      const at = (k: number) =>
        0.5 *
        (2 * p1[k] +
          (-p0[k] + p2[k]) * t +
          (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 +
          (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3);
      const x = at(0),
        y = at(1),
        a = Math.atan2((y - r.y) / r.ry, (x - r.x) / r.rx);
      const ripple = Math.sin((i + t) * 8.7) * 2.8 * Math.sin(Math.PI * t);
      smooth.push([x + Math.cos(a) * ripple, y + Math.sin(a) * ripple]);
    }
  }
  coastCache.set(r.id, smooth);
  return smooth;
}
const onBridge = (p: Vec2, margin = 0) =>
  BRIDGES.some(
    (b) =>
      Math.abs(p.x - b.x) <= b.width / 2 &&
      Math.abs(p.y - b.y) <= b.height / 2 - Math.min(margin, 18),
  );

export function contains(
  pos: Vec2,
  region: (typeof REGIONS)[number],
  margin = 20,
): boolean {
  if (
    Math.abs(pos.x - region.x) > region.rx * 0.79 ||
    Math.abs(pos.y - region.y) > region.ry * 1.03
  )
    return false;
  const points = insetCoast(region, margin);
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [x, y] = points[i],
      [px, py] = points[j];
    if (
      y > pos.y !== py > pos.y &&
      pos.x < ((px - x) * (pos.y - y)) / (py - y) + x
    )
      inside = !inside;
  }
  return inside;
}
const insetCache = new Map<string, number[][]>();
function insetCoast(r: (typeof REGIONS)[number], margin: number) {
  const key = r.id + margin;
  let points = insetCache.get(key);
  if (!points) {
    points = coast(r).map(([x, y]) => [
      r.x + (x - r.x) * (1 - margin / r.rx),
      r.y + (y - r.y) * (1 - (margin * 0.45) / r.ry),
    ]);
    insetCache.set(key, points);
  }
  return points;
}
export function regionAt(pos: Vec2): (typeof REGIONS)[number] {
  return [...REGIONS].sort(
    (a, b) => Math.abs(pos.x - a.x) - Math.abs(pos.x - b.x),
  )[0];
}
export function confineWorld(pos: Vec2, margin = 20): void {
  if (onBridge(pos, margin) || REGIONS.some((r) => contains(pos, r, margin)))
    return;
  const projected = REGIONS.flatMap((r) => {
    const points = insetCoast(r, margin + 1);
    return points.map(([x, y], i) => {
      const b = points[(i + 1) % points.length],
        dx = b[0] - x,
        dy = b[1] - y,
        t = Math.max(
          0,
          Math.min(
            1,
            ((pos.x - x) * dx + (pos.y - y) * dy) / (dx * dx + dy * dy),
          ),
        );
      return { x: x + dx * t, y: y + dy * t };
    });
  })
    .concat(
      BRIDGES.map((b) => ({
        x: Math.max(b.x - b.width / 2, Math.min(b.x + b.width / 2, pos.x)),
        y: Math.max(
          b.y - b.height / 2 + Math.min(margin, 18),
          Math.min(b.y + b.height / 2 - Math.min(margin, 18), pos.y),
        ),
      })),
    )
    .sort(
      (a, b) =>
        Math.hypot(a.x - pos.x, a.y - pos.y) -
        Math.hypot(b.x - pos.x, b.y - pos.y),
    );
  Object.assign(pos, projected[0]);
}
/** Visibility graph over authored island centers and both bridge landings. */
export function walkable(pos: Vec2, margin = 20): boolean {
  return onBridge(pos, margin) || REGIONS.some((r) => contains(pos, r, margin));
}
function clearSegment(from: Vec2, to: Vec2) {
  if (!walkable(to)) return false;
  const dx = to.x - from.x,
    dy = to.y - from.y,
    cuts = [0, 1];
  // Exact boundary crossings replace dense samples of all 128 shoreline vertices per 5px.
  // A straight segment is inside the union iff each interval between crossings is inside.
  const polygons: number[][][] = [
    ...REGIONS.map((r) => insetCoast(r, 20)),
    ...BRIDGES.map((b) => [
      [b.x - b.width / 2, b.y - 15],
      [b.x + b.width / 2, b.y - 15],
      [b.x + b.width / 2, b.y + 15],
      [b.x - b.width / 2, b.y + 15],
    ]),
  ];
  for (const polygon of polygons)
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i],
        b = polygon[(i + 1) % polygon.length],
        ex = b[0] - a[0],
        ey = b[1] - a[1],
        det = dx * ey - dy * ex;
      if (Math.abs(det) < 1e-8) continue;
      const ax = a[0] - from.x,
        ay = a[1] - from.y,
        t = (ax * ey - ay * ex) / det,
        u = (ax * dy - ay * dx) / det;
      if (t > 0 && t < 1 && u >= 0 && u <= 1) cuts.push(t);
    }
  cuts.sort((a, b) => a - b);
  for (let i = 1; i < cuts.length; i++) {
    const t = (cuts[i] + cuts[i - 1]) / 2;
    if (!walkable({ x: from.x + dx * t, y: from.y + dy * t })) return false;
  }
  return true;
}
const NAV_POINTS: Vec2[] = [
  ...REGIONS.map((r) => ({ x: r.x, y: r.y })),
  ...BRIDGES.flatMap((b) =>
    [-115, 0, 115].map((dx) => ({ x: b.x + dx, y: b.y })),
  ),
];
let navEdges: number[][] | undefined;
export function waypoint(from: Vec2, to: Vec2): Vec2 {
  if (clearSegment(from, to)) return to;
  navEdges ??= NAV_POINTS.map((a, i) =>
    NAV_POINTS.flatMap((b, j) => (i !== j && clearSegment(a, b) ? [j] : [])),
  );
  const costs = NAV_POINTS.map((p) =>
      clearSegment(from, p) ? Math.hypot(p.x - from.x, p.y - from.y) : Infinity,
    ),
    first = costs.map((n, i) => (Number.isFinite(n) ? i : -1)),
    visited = new Set<number>();
  let best = Infinity,
    next = -1;
  for (let step = 0; step < NAV_POINTS.length; step++) {
    let i = -1;
    for (let j = 0; j < costs.length; j++)
      if (!visited.has(j) && (i < 0 || costs[j] < costs[i])) i = j;
    if (i < 0 || !Number.isFinite(costs[i])) break;
    visited.add(i);
    const p = NAV_POINTS[i],
      tail = Math.hypot(p.x - to.x, p.y - to.y);
    if (costs[i] + tail < best && clearSegment(p, to)) {
      best = costs[i] + tail;
      next = first[i];
    }
    for (const j of navEdges[i]) {
      const q = NAV_POINTS[j],
        cost = costs[i] + Math.hypot(p.x - q.x, p.y - q.y);
      if (cost < costs[j]) {
        costs[j] = cost;
        first[j] = first[i];
      }
    }
  }
  return next < 0
    ? { x: regionAt(from).x, y: regionAt(from).y }
    : NAV_POINTS[next];
}
